import React, { useState } from 'react';
import { FieldTranslations } from '../../../interfaces/form-config.interface';
import { DEFAULT_LOCALES } from './form-config.utils';

interface TranslationsEditorProps {
  translations: FieldTranslations;
  onChange: (t: FieldTranslations) => void;
}

export const TranslationsEditor = ({ translations, onChange }: TranslationsEditorProps) => {
  const [extraLocales, setExtraLocales] = useState<string[]>([]);
  const [newLocale, setNewLocale] = useState('');

  const existing = Object.keys(translations);
  const allLocales = Array.from(new Set([...DEFAULT_LOCALES, ...existing, ...extraLocales]));

  const set = (locale: string, field: 'label' | 'placeholder' | 'remark', val: string) =>
    onChange({ ...translations, [locale]: { ...translations[locale], [field]: val || undefined } });

  const addLocale = () => {
    const code = newLocale.trim();
    if (!code || allLocales.includes(code)) return;
    setExtraLocales((p) => [...p, code]);
    setNewLocale('');
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Translations
        </span>
        <div className="flex gap-1">
          <input
            type="text"
            className="w-16 border border-gray-200 dark:border-gray-600 rounded px-2 py-0.5 text-xs bg-white dark:bg-gray-800"
            placeholder="ja, ko…"
            value={newLocale}
            onChange={(e) => setNewLocale(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addLocale()}
          />
          <button
            type="button"
            onClick={addLocale}
            className="px-2 py-0.5 text-xs border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            + Locale
          </button>
        </div>
      </div>
      <div className="space-y-3">
        {allLocales.map((locale) => (
          <div key={locale} className="space-y-1.5">
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">{locale}</p>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs text-gray-400 mb-0.5">Label</label>
                <input
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  placeholder={`Label (${locale})`}
                  value={translations[locale]?.label ?? ''}
                  onChange={(e) => set(locale, 'label', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-0.5">Placeholder</label>
                <input
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  placeholder={`Placeholder (${locale})`}
                  value={translations[locale]?.placeholder ?? ''}
                  onChange={(e) => set(locale, 'placeholder', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-0.5">Remark</label>
                <input
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  placeholder={`Helper text (${locale})`}
                  value={translations[locale]?.remark ?? ''}
                  onChange={(e) => set(locale, 'remark', e.target.value)}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
